import { Node, textblockTypeInputRule } from '@tiptap/core';
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react';
import { useEffect, useRef, useState } from 'react';

/**
 * ```mermaid fenced block. Source stays editable on the left; a debounced
 * preview renders on the right with the same mermaid build ADO uses
 * (mermaid 10+ syntax).
 */

let mermaidModule: Promise<typeof import('mermaid')> | null = null;
function loadMermaid() {
  if (!mermaidModule) {
    mermaidModule = import('mermaid').then(m => {
      m.default.initialize({ startOnLoad: false, securityLevel: 'strict', theme: 'neutral' });
      return m;
    });
  }
  return mermaidModule;
}

let counter = 0;

function MermaidView({ node, selected }: NodeViewProps) {
  const source = node.textContent;
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const idRef = useRef(`mermaid-${++counter}`);

  useEffect(() => {
    if (!source.trim()) {
      setSvg('');
      setError(null);
      return;
    }
    const handle = window.setTimeout(() => {
      loadMermaid()
        .then(async m => {
          await m.default.parse(source);
          const { svg } = await m.default.render(`${idRef.current}-${Date.now()}`, source);
          setSvg(svg);
          setError(null);
        })
        .catch(e => setError(String(e?.message ?? e).split('\n')[0]));
    }, 400);
    return () => window.clearTimeout(handle);
  }, [source]);

  return (
    <NodeViewWrapper className={`mermaid-block${selected ? ' is-selected' : ''}`}>
      <div className="mermaid-label" contentEditable={false}>Mermaid diagram</div>
      <div className="mermaid-body">
        <pre className="mermaid-source" spellCheck={false}>
          <NodeViewContent className="mermaid-code" />
        </pre>
        <div className="mermaid-preview" contentEditable={false}>
          {error ? <div className="mermaid-error">{error}</div> : svg ? <div dangerouslySetInnerHTML={{ __html: svg }} /> : <div className="ado-macro-hint">Preview appears as you type.</div>}
        </div>
      </div>
    </NodeViewWrapper>
  );
}

export const Mermaid = Node.create({
  name: 'mermaid',
  group: 'block',
  content: 'text*',
  marks: '',
  code: true,
  defining: true,
  isolating: true,
  // Run before codeBlock so ```mermaid fences land here.
  priority: 200,

  parseHTML() {
    return [{ tag: 'pre[data-type="mermaid"]', preserveWhitespace: 'full' }];
  },

  renderHTML() {
    return ['pre', { 'data-type': 'mermaid', class: 'mermaid-block' }, ['code', 0]];
  },

  markdownTokenName: 'code',
  parseMarkdown(token, h) {
    if ((token.lang ?? '').trim().toLowerCase() !== 'mermaid') return [];
    return h.createNode('mermaid', {}, token.text ? [h.createTextNode(token.text)] : []);
  },
  renderMarkdown(node, h) {
    const body = node.content ? h.renderChildren(node.content) : '';
    return ['```mermaid', body, '```'].join('\n');
  },

  addKeyboardShortcuts() {
    return {
      // Enter inserts a newline inside the diagram source instead of splitting the block.
      Enter: () => {
        if (!this.editor.isActive(this.name)) return false;
        return this.editor.commands.insertContent('\n');
      },
      // Mod-Enter leaves the block.
      'Mod-Enter': () => {
        if (!this.editor.isActive(this.name)) return false;
        return this.editor.commands.exitCode();
      },
      'Shift-Enter': () => {
        if (!this.editor.isActive(this.name)) return false;
        return this.editor.commands.exitCode();
      },
    };
  },

  addInputRules() {
    return [textblockTypeInputRule({ find: /^```mermaid[\s\n]$/, type: this.type })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MermaidView);
  },
});
